// ==========================
// Version 3 — pages/Privacy.js (confirmed controller and contact details)
// ==========================
import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import "./LegalPage.css";

const EFFECTIVE_DATE = "13 August 2026";

export default function Privacy() {
  const pageUrl =
    typeof window !== "undefined" ? `${window.location.origin}/privacy` : undefined;

  return (
    <div className="legal-page">
      <Seo
        title="Privacy Notice • Blessed with a Kiss"
        description="How Blessed with a Kiss collects, uses, shares, and protects your information."
        image="/logo512.png"
        url={pageUrl}
        type="website"
      />

      <header className="legal-page__hero">
        <div className="legal-page__hero-inner">
          <p className="legal-page__eyebrow">Your memories deserve clarity</p>
          <h1>Privacy Notice</h1>
          <p className="legal-page__intro">
            This notice explains what information Blessed with a Kiss uses, why we use it,
            and what changes when you choose to immortalise a memory.
          </p>
          <p className="legal-page__date">Effective: {EFFECTIVE_DATE}</p>
        </div>
      </header>

      <div className="legal-page__layout">
        <nav className="legal-page__toc" aria-label="Privacy notice contents">
          <strong>On this page</strong>
          <a href="#privacy-overview">In short</a>
          <a href="#privacy-who">Who we are</a>
          <a href="#privacy-data">Information we use</a>
          <a href="#privacy-why">Why we use it</a>
          <a href="#privacy-visibility">Visibility and minting</a>
          <a href="#privacy-sharing">Who receives it</a>
          <a href="#privacy-retention">How long we keep it</a>
          <a href="#privacy-rights">Your rights</a>
          <a href="#privacy-contact">Contact and changes</a>
        </nav>

        <article className="legal-page__document">
          <section id="privacy-overview" className="legal-page__summary" aria-labelledby="privacy-overview-title">
            <h2 id="privacy-overview-title">In short</h2>
            <ul>
              <li>Private, unminted memories are intended to remain visible only to you.</li>
              <li>You decide whether an unminted memory and its media are public.</li>
              <li>Minting requires the memory, media, story, and location to become public.</li>
              <li>Minted information may remain permanently available on IPFS and a public blockchain.</li>
              <li>Blessed with a Kiss is for adults aged 18 or over.</li>
            </ul>
          </section>

          <section id="privacy-who">
            <h2>1. Who we are</h2>
            <p>
              Blessed with a Kiss is operated by Afrooz Innovations Ltd, company number 17080521,
              registered in England and Wales. Afrooz Innovations Ltd is the controller responsible
              for the personal information described in this notice. “BWK”, “we”, and “us” refer to
              Afrooz Innovations Ltd and the Blessed with a Kiss service. The service is available
              to users worldwide.
            </p>
            <aside className="legal-page__notice">
              Privacy questions and requests can be sent to{" "}
              <a href="mailto:privacy@blessedwithakiss.com">
                privacy@blessedwithakiss.com
              </a>.
            </aside>
          </section>

          <section id="privacy-data">
            <h2>2. Information we use</h2>
            <h3>Account and profile information</h3>
            <p>
              This may include your email address, authentication provider, email-verification
              status, display name, profile image, account preferences, and internal user ID.
            </p>

            <h3>Memory content</h3>
            <p>
              We process the titles, stories, dates, times, photographs, visibility settings, and
              other details you choose to include in a memory.
            </p>

            <h3>Location information</h3>
            <p>
              If you choose to add a location, we process its label and coordinates. We may derive
              a city, region, or similar place description to display the memory on a map. You
              should not add a location if you do not want it processed with the memory.
            </p>

            <h3>Wallet and blockchain information</h3>
            <p>
              If you link a wallet or mint a memory, we process the public wallet address,
              wallet-linking signature, network, transaction status, transaction hash, token URI,
              and related public blockchain information. We do not receive or store your wallet
              private key or seed phrase.
            </p>

            <h3>Technical, security, and usage information</h3>
            <p>
              Firebase, reCAPTCHA Enterprise, App Check, and analytics technologies may process
              device, browser, IP address, interaction, diagnostic, and security information. We
              may also process reports, moderation decisions, and support communications.
            </p>
          </section>

          <section id="privacy-why">
            <h2>3. Why we use information</h2>
            <div className="legal-page__table-wrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Purpose</th>
                    <th scope="col">Typical legal basis</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Create and secure your account, save memories, and provide requested features</td>
                    <td>Performance of our contract with you</td>
                  </tr>
                  <tr>
                    <td>Link a wallet and carry out a mint you request</td>
                    <td>Performance of our contract and steps you ask us to take</td>
                  </tr>
                  <tr>
                    <td>Protect users, prevent misuse, investigate reports, and maintain security</td>
                    <td>Our legitimate interests and compliance with legal obligations</td>
                  </tr>
                  <tr>
                    <td>Diagnose faults and understand how the service performs</td>
                    <td>Our legitimate interests, or consent where applicable law requires it</td>
                  </tr>
                  <tr>
                    <td>Comply with law and respond to valid legal requests</td>
                    <td>Compliance with legal obligations</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              Where we rely on legitimate interests, we consider the effect on your rights and use
              information only where those interests are not overridden by your rights.
            </p>
          </section>

          <section id="privacy-visibility">
            <h2>4. Private, public, and minted memories</h2>
            <h3>Unminted memories</h3>
            <p>
              You can choose whether an unminted memory is private or visible to signed-in BWK
              users. Media has a separate visibility choice. Private pins and private memory
              content are intended to be available only to the memory owner.
            </p>
            <p>
              You may edit or delete an unminted memory through the service. Deletion removes the
              active memory record and associated BWK-hosted media, subject to limited backup,
              security, and legal-retention needs.
            </p>

            <aside className="legal-page__warning">
              <strong>Minting is public and cannot be undone.</strong>
              <p>
                Before minting, BWK requires the memory, its media, story, and saved location to be
                public. NFT metadata may include the image, a story excerpt, memory date and time,
                location label, latitude and longitude, an internal memory identifier, and your
                public wallet address. Coordinates may identify the exact place you selected.
              </p>
              <p>
                The NFT image and metadata are uploaded to IPFS and the proof is recorded on a
                public blockchain. Other people may copy, index, or redistribute that information.
                BWK cannot edit or reliably delete information already published through these
                decentralised systems, even if the website, your account, or the BWK-hosted copy is
                later removed.
              </p>
            </aside>
          </section>

          <section id="privacy-sharing">
            <h2>5. Who receives information</h2>
            <p>We use providers that help operate the service, including:</p>
            <ul>
              <li>Google Firebase for authentication, databases, file storage, hosting, functions, analytics, and security;</li>
              <li>Google Maps for maps, place selection, and location descriptions;</li>
              <li>Google reCAPTCHA Enterprise and Firebase App Check for abuse prevention;</li>
              <li>Thirdweb and WalletConnect for wallet and blockchain connectivity;</li>
              <li>Pinata and public IPFS gateways for NFT images and metadata; and</li>
              <li>public blockchain networks, RPC providers, wallet providers, and block explorers.</li>
            </ul>
            <p>
              These providers may process information in countries outside your own. Where data
              protection law requires safeguards for an international transfer, we will rely on an
              approved adequacy decision, contractual protections, or another lawful transfer
              mechanism. Public blockchain and IPFS publication is global by its nature.
            </p>
            <p>
              We may also disclose information where required by law, to protect users or the
              service, or as part of a business reorganisation. We do not sell your personal
              information.
            </p>
          </section>

          <section id="privacy-retention">
            <h2>6. How long we keep information</h2>
            <p>
              A retention period is the length of time information remains stored. We currently use
              these retention principles:
            </p>
            <ul>
              <li>Account and profile information is generally kept while your account remains active.</li>
              <li>Unminted memories and BWK-hosted media are kept until you delete them or your account is closed.</li>
              <li>Security, transaction, support, and moderation records are kept only as long as reasonably needed for their purpose, dispute handling, safety, and legal obligations.</li>
              <li>Deleted information may remain temporarily in restricted backups before those backups are overwritten.</li>
              <li>Minted images, metadata, wallet activity, and blockchain transactions may remain publicly available indefinitely.</li>
            </ul>
            <p>
              We will publish a more detailed retention schedule after the operational periods for
              backups, security logs, reports, and closed accounts have been approved.
            </p>
          </section>

          <section id="privacy-rights">
            <h2>7. Your choices and rights</h2>
            <p>
              Depending on where you live, you may have rights to access, correct, delete, restrict,
              or receive a copy of your personal information, and to object to certain uses. Where
              processing relies on consent, you may withdraw it for future processing. You may also
              complain to your local data-protection authority. UK users may complain to the
              Information Commissioner&apos;s Office.
            </p>
            <p>
              These rights can apply to information controlled by BWK, but they cannot make a valid
              public blockchain transaction disappear or guarantee deletion of content already
              distributed through IPFS or copied by others.
            </p>
            <p>
              You can change the visibility of an unminted memory and delete an unminted memory from
              within the service. Account-level data access and deletion tools are planned. Until
              they are available, contact{" "}
              <a href="mailto:privacy@blessedwithakiss.com">
                privacy@blessedwithakiss.com
              </a>.
            </p>
          </section>

          <section>
            <h2>8. Adults only</h2>
            <p>
              BWK is intended only for people aged 18 or over. We do not knowingly permit children
              to create accounts. If we learn that an account belongs to someone under 18, we may
              close the account and remove information we control, subject to legal and technical
              limitations affecting public blockchain and IPFS records.
            </p>
          </section>

          <section>
            <h2>9. Security</h2>
            <p>
              We use technical and organisational measures intended to protect information,
              including authentication, email verification, access rules, and abuse-prevention
              tools. No online service can guarantee absolute security. Keep your account and wallet
              credentials secure and never share your wallet seed phrase or private key.
            </p>
          </section>

          <section id="privacy-contact">
            <h2>10. Contact and changes</h2>
            <p>
              We may update this notice as the service, providers, or legal requirements change. We
              will change the effective date and provide an appropriate notice when an update is
              significant.
            </p>
            <p>
              Send privacy questions or rights requests to{" "}
              <a href="mailto:privacy@blessedwithakiss.com">
                privacy@blessedwithakiss.com
              </a>. For general product support, email{" "}
              <a href="mailto:support@blessedwithakiss.com">
                support@blessedwithakiss.com
              </a>. Please do not include wallet private keys, seed phrases, or unnecessary
              sensitive information in a request.
            </p>
          </section>

          <div className="legal-page__actions">
            <Link to="/" className="legal-page__button legal-page__button--secondary">Return home</Link>
            <Link to="/terms" className="legal-page__button legal-page__button--primary">Read the Terms</Link>
          </div>
        </article>
      </div>
    </div>
  );
}

// ==========================
// End Version 3 — pages/Privacy.js
// ==========================
